import { Link } from "react-router-dom";
import {
  Stethoscope, MapPin, Phone, Mail, GraduationCap, Award,
  Clock, Sparkles, Quote, PlayCircle, ExternalLink, ArrowRight, CheckCircle2,
  Heart, Trophy, CalendarClock,
} from "lucide-react";
import SEO from "../components/SEO";
import JsonLd, { breadcrumbLd, physicianLd } from "../components/JsonLd";
import { FEATURED_DOCTOR as D } from "../data/featuredDoctor";
import AppointmentForm from "../components/AppointmentForm";

export default function Doctors() {
  const ld = {
    ...physicianLd({
      name: D.name,
      specialty: D.specialties[0],
      city: "New Delhi",
      state: "Delhi",
      profile_photo: D.photo,
      bio: D.bio[0],
    }),
    alumniOf: D.qualifications,
    memberOf: D.memberships.map((m) => ({ "@type": "Organization", name: m })),
    telephone: D.contact.phones,
    email: D.contact.email,
  };

  return (
    <>
      <SEO
        title={`${D.name} — Senior ENT & Cochlear Implant Surgeon, Delhi`}
        description={`${D.name}, ${D.position}, Max Healthcare Delhi. ${D.years_experience} of experience in cochlear implant surgery, skull-base surgery and paediatric hearing restoration.`}
      />
      <JsonLd id="ld-physician" data={ld} />
      <JsonLd
        id="ld-breadcrumb"
        data={breadcrumbLd([
          { name: "Home", path: "/" },
          { name: "Doctors", path: "/doctors" },
          { name: D.name, path: "/doctors" },
        ])}
      />

      {/* ---------------- HERO ---------------- */}
      <section className="bg-gradient-to-br from-[#F0F9FF] via-white to-[#ECFDF5] py-14 md:py-20">
        <div className="hc-container grid lg:grid-cols-5 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-3">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#0F766E]/10 text-[#0F766E] text-[13px] font-semibold rounded-full mb-5">
              <Sparkles size={14} /> Our Featured Cochlear Implant Expert
            </div>
            <h1
              data-testid="doctor-name"
              className="font-serif text-[2.25rem] md:text-[3.1rem] font-semibold leading-[1.05] tracking-tight text-slate-900"
            >
              {D.name}
            </h1>
            <div className="mt-3 text-[#0369A1] font-semibold text-[1.05rem]">{D.title}</div>
            <div className="mt-1 text-slate-500 text-[15px]">{D.position}</div>

            <div className="mt-6 flex flex-wrap gap-2">
              <span className="chip bg-[#F0F9FF] text-[#0369A1]">
                <Clock size={13} /> {D.years_experience} experience
              </span>
              <span className="chip bg-[#ECFDF5] text-[#0F766E]">
                <Award size={13} /> Cochlear Implant Group of India
              </span>
              <span className="chip bg-amber-50 text-amber-700">
                <Stethoscope size={13} /> Skull Base Society of India
              </span>
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link data-testid="doctor-eligibility-cta" to="/cochlear-implant-eligibility" className="btn-primary">
                Check Eligibility <ArrowRight size={18} className="ml-1.5" />
              </Link>
              <Link data-testid="doctor-counsellor-cta" to="/register" className="btn-secondary">
                Speak to a HearClear Counsellor
              </Link>
            </div>
          </div>

          <div className="lg:col-span-2">
            <div className="relative rounded-3xl overflow-hidden shadow-xl aspect-[4/5] bg-slate-100">
              <img
                data-testid="doctor-photo"
                src={D.photo}
                alt={D.photoAlt}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ---------------- BIO ---------------- */}
      <section className="hc-section">
        <div className="hc-container grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 space-y-4">
            <div className="text-[#0F766E] uppercase tracking-wider text-[12px] font-bold">About Dr. Sachdeva</div>
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-slate-900 leading-tight">
              Three-and-a-half decades of ear, hearing & implant expertise
            </h2>
            {D.bio.map((p, i) => (
              <p key={i} className="text-slate-700 leading-relaxed text-[17px]">{p}</p>
            ))}
            <p className="text-[13px] text-slate-500 mt-4">
              Public information referenced from{" "}
              <a href={D.clinicSourceUrl} target="_blank" rel="noopener noreferrer" className="text-[#0369A1] hover:underline inline-flex items-center gap-1">
                drssachdeva.com <ExternalLink size={12} />
              </a>{" "}
              and{" "}
              <a href={D.maxProfileUrl} target="_blank" rel="noopener noreferrer" className="text-[#0369A1] hover:underline inline-flex items-center gap-1">
                Max Healthcare <ExternalLink size={12} />
              </a>
              .
            </p>
          </div>

          <aside className="space-y-5">
            <div className="card-hc" data-testid="doctor-qualifications">
              <span className="w-11 h-11 rounded-xl bg-[#F0F9FF] flex items-center justify-center text-[#0369A1] mb-3">
                <GraduationCap size={20} />
              </span>
              <div className="font-serif text-lg font-semibold text-slate-900">Qualifications & training</div>
              <ul className="mt-3 space-y-2 text-[14.5px]">
                {D.qualifications.map((q, i) => (
                  <li key={i} className="flex gap-2 text-slate-700">
                    <CheckCircle2 size={16} className="text-[#0F766E] shrink-0 mt-0.5" /> {q}
                  </li>
                ))}
              </ul>
            </div>

            <div className="card-hc" data-testid="doctor-affiliations">
              <span className="w-11 h-11 rounded-xl bg-[#ECFDF5] flex items-center justify-center text-[#0F766E] mb-3">
                <MapPin size={20} />
              </span>
              <div className="font-serif text-lg font-semibold text-slate-900">Practice locations</div>
              <ul className="mt-3 space-y-2 text-[14.5px]">
                {D.affiliations.map((a, i) => (
                  <li key={i} className="flex gap-2 text-slate-700">
                    <MapPin size={16} className="text-slate-400 shrink-0 mt-0.5" /> {a}
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </section>

      {/* ---------------- SPECIALTIES ---------------- */}
      <section className="hc-section bg-slate-50">
        <div className="hc-container">
          <div className="max-w-3xl">
            <div className="text-[#0F766E] uppercase tracking-wider text-[12px] font-bold mb-3">Areas of expertise</div>
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-slate-900 leading-tight">
              Specialised ENT & hearing care
            </h2>
          </div>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {D.specialties.map((s, i) => (
              <div key={i} data-testid={`specialty-${i}`} className="bg-white rounded-2xl border border-slate-200 p-5 flex items-start gap-3">
                <span className="w-10 h-10 rounded-lg bg-[#F0F9FF] flex items-center justify-center text-[#0369A1] shrink-0">
                  <Stethoscope size={18} />
                </span>
                <div className="font-serif text-[1.05rem] text-slate-900 font-semibold leading-snug">{s}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- VIDEOS ---------------- */}
      <section className="hc-section">
        <div className="hc-container">
          <div className="max-w-3xl">
            <div className="text-[#0F766E] uppercase tracking-wider text-[12px] font-bold mb-3">Videos & explanations</div>
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-slate-900 leading-tight">
              Dr. Sanjay Sachdeva explains cochlear implants
            </h2>
            <p className="mt-4 text-slate-700 text-[17px] leading-relaxed">
              Short expert explainers and patient stories — in Dr. Sachdeva's own words.
            </p>
          </div>

          <div className="mt-10 grid md:grid-cols-2 gap-6">
            {D.videos.map((v, i) => (
              <div key={i} data-testid={`doctor-video-${i}`} className="bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-md transition">
                <div className="aspect-video bg-black">
                  <video
                    controls
                    preload="metadata"
                    playsInline
                    className="w-full h-full"
                    poster={v.poster}
                  >
                    <source src={v.url} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                </div>
                <div className="p-5">
                  <div className="flex items-start gap-2">
                    <PlayCircle size={18} className="text-[#0369A1] shrink-0 mt-1" />
                    <div>
                      <div className="font-serif text-[1.05rem] text-slate-900 font-semibold leading-snug">{v.title}</div>
                      <p className="mt-1 text-slate-600 text-[14px] leading-relaxed">{v.caption}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- TESTIMONIALS ---------------- */}
      <section className="hc-section bg-slate-50">
        <div className="hc-container">
          <div className="max-w-3xl">
            <div className="text-[#0F766E] uppercase tracking-wider text-[12px] font-bold mb-3">Patient testimonials</div>
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-slate-900 leading-tight">
              What patients say about Dr. Sachdeva
            </h2>
            <p className="mt-3 text-slate-500 text-[13px]">
              Reviews sourced from Dr. Sachdeva's public clinic website.
            </p>
          </div>

          <div className="mt-10 grid md:grid-cols-3 gap-5">
            {D.testimonials.map((t, i) => (
              <div key={i} data-testid={`testimonial-${i}`} className="card-hc">
                <Quote size={22} className="text-[#0369A1]/40" />
                <p className="mt-3 text-slate-700 leading-relaxed text-[15px]">"{t.quote}"</p>
                <div className="mt-5 pt-4 border-t border-slate-100 font-semibold text-slate-900 text-[14.5px]">
                  — {t.name}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- SWITCH-ON MOMENTS (Case Gallery) ---------------- */}
      <section className="hc-section" data-testid="switch-on-section">
        <div className="hc-container">
          <div className="max-w-3xl">
            <div className="text-[#0F766E] uppercase tracking-wider text-[12px] font-bold mb-3 inline-flex items-center gap-2">
              <Heart size={14} /> Switch-on Moments
            </div>
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-slate-900 leading-tight">
              Small moments that changed everything
            </h2>
            <p className="mt-4 text-slate-700 text-[17px] leading-relaxed">
              Composite, anonymised stories from paediatric cases in Dr. Sachdeva's practice —
              the first "Amma", the first response to a doorbell, the first rhyme at school.
              These are the moments the whole team works towards.
            </p>
          </div>

          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {D.switch_on_moments.map((m, i) => (
              <div
                key={i}
                data-testid={`switch-on-moment-${i}`}
                className="bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-md transition"
              >
                <div className="aspect-[4/3] overflow-hidden bg-slate-100">
                  <img src={m.image} alt={`Illustrative image — ${m.moment}`} className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 text-[12px] text-slate-500">
                    <MapPin size={12} /> {m.city}
                  </div>
                  <div className="mt-1 font-serif text-[1.05rem] text-slate-900 font-semibold leading-snug">
                    {m.alias} · {m.moment}
                  </div>
                  <p className="mt-2 text-slate-600 text-[14px] leading-relaxed">{m.story}</p>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-6 text-[12.5px] text-slate-500 max-w-3xl">
            Note: Names, ages and photographs shown are illustrative composites inspired by real paediatric
            outcomes. No identifiable patient information is used. Actual results depend on candidacy,
            device programming and post-implant rehabilitation.
          </p>
        </div>
      </section>

      {/* ---------------- CAREER HIGHLIGHTS (Timeline) ---------------- */}
      <section className="hc-section bg-slate-50" data-testid="career-highlights-section">
        <div className="hc-container">
          <div className="max-w-3xl">
            <div className="text-[#0F766E] uppercase tracking-wider text-[12px] font-bold mb-3 inline-flex items-center gap-2">
              <Trophy size={14} /> Career Highlights
            </div>
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-slate-900 leading-tight">
              Training, appointments & recognition
            </h2>
            <p className="mt-4 text-slate-700 text-[17px] leading-relaxed">
              A publicly documented career built around ear, hearing and skull-base surgery — with
              international fellowships from the founding institutes of the field.
            </p>
          </div>

          <ol className="mt-10 relative border-l-2 border-[#0369A1]/20 pl-6 space-y-8 max-w-3xl">
            {D.career_highlights.map((c, i) => (
              <li key={i} data-testid={`career-item-${i}`} className="relative">
                <span className="absolute -left-[35px] top-1 w-4 h-4 rounded-full bg-[#0369A1] ring-4 ring-[#F0F9FF]" />
                <div className="text-[12px] font-bold uppercase tracking-wider text-[#0F766E]">{c.year}</div>
                <div className="font-serif text-[1.15rem] text-slate-900 font-semibold mt-1 leading-snug">
                  {c.title}
                </div>
                <p className="mt-1.5 text-slate-700 text-[15px] leading-relaxed">{c.detail}</p>
              </li>
            ))}
          </ol>
          <p className="mt-6 text-[12.5px] text-slate-500 max-w-3xl">
            Assembled from publicly available profiles at drssachdeva.com and Max Healthcare. Years for older
            entries are given as decades where exact dates are not publicly listed.
          </p>
        </div>
      </section>

      {/* ---------------- APPOINTMENT FORM ---------------- */}
      <section className="hc-section" data-testid="appointment-section" id="request-appointment">
        <div className="hc-container grid lg:grid-cols-5 gap-10">
          <div className="lg:col-span-2">
            <div className="text-[#0F766E] uppercase tracking-wider text-[12px] font-bold mb-3 inline-flex items-center gap-2">
              <CalendarClock size={14} /> Book a consultation
            </div>
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-slate-900 leading-tight">
              Request a slot with Dr. Sanjay Sachdeva
            </h2>
            <p className="mt-4 text-slate-700 text-[16.5px] leading-relaxed">
              Send us your basic details and any hearing reports you already have. A HearClear counsellor
              will call you within 1 working day to confirm your slot, share the pre-consultation
              checklist and — if needed — help you plan travel to Delhi.
            </p>
            <ul className="mt-6 space-y-3 text-[15px] text-slate-700">
              <li className="flex items-start gap-2"><CheckCircle2 size={18} className="text-[#0F766E] shrink-0 mt-0.5" /> No consultation fees are charged for the initial coordination call.</li>
              <li className="flex items-start gap-2"><CheckCircle2 size={18} className="text-[#0F766E] shrink-0 mt-0.5" /> Reports and case details stay confidential.</li>
              <li className="flex items-start gap-2"><CheckCircle2 size={18} className="text-[#0F766E] shrink-0 mt-0.5" /> In-person (Delhi) and video consultations both available.</li>
            </ul>
          </div>
          <div className="lg:col-span-3">
            <AppointmentForm />
          </div>
        </div>
      </section>

      {/* ---------------- CONTACT / CTA ---------------- */}
      <section className="hc-section">
        <div className="hc-container">
          <div className="bg-gradient-to-br from-[#0369A1] to-[#0F766E] text-white rounded-3xl p-8 md:p-14 grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="font-serif text-3xl md:text-4xl font-semibold leading-tight">
                Consult with Dr. Sanjay Sachdeva
              </h2>
              <p className="mt-4 text-sky-50 text-[17px] leading-relaxed max-w-xl">
                Send your details to HearClear — we will help you book a consultation at the most
                convenient location and prepare the reports Dr. Sachdeva's team will need.
              </p>
              <div className="mt-7 flex flex-wrap gap-3">
                <a
                  data-testid="doctor-book-cta"
                  href="#request-appointment"
                  className="bg-white text-[#0369A1] hover:bg-sky-50 font-semibold rounded-xl px-7 py-3.5 inline-flex items-center gap-1.5"
                >
                  Request consultation <ArrowRight size={18} />
                </a>
                <a
                  data-testid="doctor-call-cta"
                  href={`tel:${D.contact.phones[0].replace(/\s/g, "")}`}
                  className="border-2 border-white/40 hover:bg-white/10 text-white rounded-xl px-7 py-3.5 inline-flex items-center gap-2 font-medium"
                >
                  <Phone size={18} /> {D.contact.phones[0]}
                </a>
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur border border-white/15 rounded-2xl p-6 space-y-3 text-[15px]">
              <div className="flex items-start gap-3">
                <MapPin size={18} className="text-sky-100 shrink-0 mt-0.5" />
                <div>{D.contact.clinic_address}</div>
              </div>
              <div className="flex items-start gap-3">
                <Clock size={18} className="text-sky-100 shrink-0 mt-0.5" />
                <div>{D.contact.clinic_hours}</div>
              </div>
              <div className="flex items-start gap-3">
                <Mail size={18} className="text-sky-100 shrink-0 mt-0.5" />
                <a href={`mailto:${D.contact.email}`} className="hover:underline">{D.contact.email}</a>
              </div>
              <div className="flex items-start gap-3">
                <Phone size={18} className="text-sky-100 shrink-0 mt-0.5" />
                <div>
                  {D.contact.phones.map((p, i) => (
                    <div key={i}>{p}</div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
